import React, { useState, useRef, useEffect, forwardRef } from "react";
import { createPortal } from "react-dom";
import clsx from "clsx";
import { Search, ChevronDown, X } from "lucide-react";

export interface SearchableSelectProps {
  options: { value: string; label: string }[];
  value?: string;
  onChange?: (value: string) => void;
  onBlur?: React.FocusEventHandler<HTMLInputElement | HTMLSelectElement>;
  name?: string;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  id?: string;
}

/**
 * SearchableSelect
 * A searchable dropdown select component optimized for city selection.
 * Allows users to search through options by typing.
 */
export const SearchableSelect = forwardRef<HTMLSelectElement, SearchableSelectProps>(({
      options,
      value,
      onChange,
      onBlur,
      name,
      placeholder = "Buscar ciudad...",
      className,
      disabled = false,
      id,
    }, ref) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [selectedLabel, setSelectedLabel] = useState("");
    const [dropdownPosition, setDropdownPosition] = useState<{
      top: number;
      left: number;
      width: number;
      openUpward: boolean;
      maxHeight: number;
    } | null>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const isSelectingRef = useRef(false);

    // Detecta layout mobile vs desktop (problemas con position: fixed + teclado en iOS)
    const [isMobileLayout, setIsMobileLayout] = useState(false);

    useEffect(() => {
      const checkLayout = () => {
        setIsMobileLayout(window.innerWidth < 768); // breakpoint md
      };

      checkLayout();
      window.addEventListener("resize", checkLayout);
      return () => window.removeEventListener("resize", checkLayout);
    }, []);

    // Find selected option label
    useEffect(() => {
      const selected = options.find((opt) => opt.value === value);
      setSelectedLabel(selected?.label || "");
      if (!value) {
        setSearchTerm("");
      }
    }, [value, options]);

    // Filter options based on search term
    const filteredOptions = options.filter((option) =>
      option.label.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Handle click outside to close dropdown
    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        // Don't close if we're in the process of selecting
        if (isSelectingRef.current) {
          isSelectingRef.current = false;
          return;
        }

        const target = event.target as Node;
        if (
          containerRef.current &&
          !containerRef.current.contains(target) &&
          dropdownRef.current &&
          !dropdownRef.current.contains(target)
        ) {
          setIsOpen(false);
          setSearchTerm("");
        }
      };

      if (isOpen) {
        // Use a small delay to allow click events to fire before checking outside
        const timeoutId = setTimeout(() => {
          document.addEventListener("mousedown", handleClickOutside);
        }, 0);

        return () => {
          clearTimeout(timeoutId);
          document.removeEventListener("mousedown", handleClickOutside);
        };
      }
    }, [isOpen]);

    // Calculate dropdown position (desktop) – siempre abre hacia abajo
    const calculatePosition = React.useCallback(() => {
      if (!containerRef.current) return;

      const rect = containerRef.current.getBoundingClientRect();
      const DROPDOWN_MAX_HEIGHT = 240; // max-h-60
      const MARGIN = 8;

      const spaceBelow = window.innerHeight - rect.bottom;

      // Open downward - position below the input
      const top = rect.bottom + 4; // mt-1 = 4px
      const maxHeight = Math.max(
        100,
        Math.min(DROPDOWN_MAX_HEIGHT, spaceBelow - MARGIN)
      );

      // Ensure dropdown stays within viewport horizontally
      const width = rect.width;
      const left = Math.max(
        MARGIN,
        Math.min(rect.left, window.innerWidth - width - MARGIN)
      );

      setDropdownPosition({
        top,
        left,
        width,
        openUpward: false,
        maxHeight,
      });
    }, []);

    useEffect(() => {
      if (isOpen && !isMobileLayout) {
        // Solo necesitamos posicionamiento especial en desktop (fixed + portal)
        calculatePosition();

        // Recalculate on scroll and resize
        const handleUpdate = () => calculatePosition();
        window.addEventListener("scroll", handleUpdate, true);
        window.addEventListener("resize", handleUpdate);

        return () => {
          window.removeEventListener("scroll", handleUpdate, true);
          window.removeEventListener("resize", handleUpdate);
        };
      } else if (!isOpen) {
        setDropdownPosition(null);
      }
    }, [isOpen, isMobileLayout, calculatePosition]);

    // Focus input when dropdown opens
    useEffect(() => {
      if (isOpen && inputRef.current) {
        inputRef.current.focus();
      }
    }, [isOpen]);

    const handleSelect = (optionValue: string) => {
      isSelectingRef.current = true;
      onChange?.(optionValue);
      setIsOpen(false);
      setSearchTerm("");
      // Reset after a short delay
      setTimeout(() => {
        isSelectingRef.current = false;
      }, 100);
    };

    const handleClear = (e: React.MouseEvent) => {
      e.stopPropagation();
      onChange?.("");
      setSearchTerm("");
      if (inputRef.current) {
        inputRef.current.focus();
      }
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearchTerm(e.target.value);
      if (!isOpen) {
        setIsOpen(true);
      }
    };

    const handleInputFocus = () => {
      setIsOpen(true);
    };

    const displayValue = isOpen ? searchTerm : selectedLabel;

    return (
      <div ref={containerRef} className="relative w-full">
        {/* Hidden select for form compatibility */}
        <select
          ref={ref}
          name={name}
          value={value || ""}
          onChange={() => {}} // Controlled by SearchableSelect
          onBlur={onBlur}
          className="sr-only"
          tabIndex={-1}
          aria-hidden="true"
          disabled={disabled}
        >
          <option value="">{placeholder}</option>
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>

        {/* Visible searchable input */}
        <div
          className={clsx(
            "w-full p-2 border rounded-md",
            "flex items-center gap-2",
            disabled ? "bg-gray-100 border-gray-300 cursor-not-allowed" : "bg-white border-gray-300",
            isOpen && !disabled && "ring-2 ring-blue-500 border-blue-500",
            className
          )}
          onClick={() => !disabled && !isOpen && setIsOpen(true)}
        >
          <Search size={16} className={clsx("shrink-0", disabled ? "text-gray-300" : "text-gray-400")} />
          <input
            id={id}
            ref={inputRef}
            type="text"
            value={displayValue}
            onChange={handleInputChange}
            onFocus={handleInputFocus}
            onBlur={(e) => {
              // Only trigger blur if we're not selecting from dropdown
              if (!isSelectingRef.current) {
                onBlur?.(e);
              }
            }}
            placeholder={placeholder}
            disabled={disabled}
            className={clsx(
              "flex-1 outline-none bg-transparent text-sm",
              disabled && "text-gray-500 placeholder:text-gray-400"
            )}
          />
          {value && !disabled && (
            <button
              type="button"
              onClick={handleClear}
              className="text-gray-400 hover:text-gray-600 shrink-0"
              aria-label="Limpiar selección"
            >
              <X size={16} />
            </button>
          )}
          <ChevronDown
            size={16}
            className={clsx(
              "text-gray-400 shrink-0 transition-transform",
              isOpen && "rotate-180"
            )}
          />
        </div>

        {/* Dropdown menu */}
        {isOpen &&
          (isMobileLayout ? (
            // En mobile: dropdown absoluto dentro del contenedor, justo debajo del input
            <div
              ref={dropdownRef}
              className={clsx(
                "absolute z-[100] left-0 right-0 mt-1",
                "bg-white border border-gray-300 rounded-md shadow-lg",
                "overflow-auto"
              )}
              style={{ maxHeight: 240 }}
              onClick={(e) => e.stopPropagation()}
            >
              {filteredOptions.length === 0 ? (
                <div className="p-3 text-sm text-gray-500 text-center">
                  No se encontraron ciudades
                </div>
              ) : (
                filteredOptions.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    onMouseDown={(e) => {
                      e.preventDefault(); // Prevent input blur before click
                      handleSelect(option.value);
                    }}
                    className={clsx(
                      "w-full px-3 py-2 text-left text-sm hover:bg-gray-100 transition-colors",
                      "flex items-center gap-2",
                      value === option.value && "bg-blue-50 text-blue-700"
                    )}
                  >
                    <span className="flex-1">{option.label}</span>
                  </button>
                ))
              )}
            </div>
          ) : (
            // Dropdown menu - rendered in portal to escape modal overflow
            dropdownPosition &&
            createPortal(
              <div
                ref={dropdownRef}
                className={clsx(
                  "fixed z-[100] bg-white border border-gray-300 rounded-md shadow-lg",
                  "overflow-auto"
                )}
                style={{
                  top: `${dropdownPosition.top}px`,
                  left: `${dropdownPosition.left}px`,
                  width: `${dropdownPosition.width}px`,
                  maxHeight: `${dropdownPosition.maxHeight}px`,
                }}
                onClick={(e) => e.stopPropagation()}
              >
                {filteredOptions.length === 0 ? (
                  <div className="p-3 text-sm text-gray-500 text-center">
                    No se encontraron ciudades
                  </div>
                ) : (
                  filteredOptions.map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      onMouseDown={(e) => {
                        e.preventDefault(); // Prevent input blur before click
                        handleSelect(option.value);
                      }}
                      className={clsx(
                        "w-full px-3 py-2 text-left text-sm hover:bg-gray-100 transition-colors",
                        "flex items-center gap-2",
                        value === option.value && "bg-blue-50 text-blue-700"
                      )}
                    >
                      <span className="flex-1">{option.label}</span>
                    </button>
                  ))
                )}
              </div>,
              document.body
            )
          ))}
      </div>
    );
  });

SearchableSelect.displayName = "SearchableSelect";

